import React from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  Pressable,
  StyleSheet,
  ActivityIndicator,
  Platform,
} from 'react-native';
import { EDITOR_COLORS, editorText, webCheckboxStyle } from '../../../styles/editorTheme';

export interface ManageAccountsSearchValues {
  accountName: string;
  accountId: string;
  showAll: boolean;
}

interface ManageAccountsSearchBarProps {
  values: ManageAccountsSearchValues;
  onValuesChange: (values: ManageAccountsSearchValues) => void;
  onSearch: (values: ManageAccountsSearchValues) => void;
  isLoading: boolean;
  onAddNewAccount: () => void;
}

const EMPTY: ManageAccountsSearchValues = {
  accountName: '',
  accountId: '',
  showAll: false,
};

export const ManageAccountsSearchBar: React.FC<ManageAccountsSearchBarProps> = ({
  values,
  onValuesChange,
  onSearch,
  isLoading,
  onAddNewAccount,
}) => {
  const setField = (field: keyof ManageAccountsSearchValues, value: string | boolean) => {
    onValuesChange({ ...values, [field]: value });
  };

  const handleSearch = () => {
    onSearch(values);
  };

  const handleClear = () => {
    onValuesChange(EMPTY);
  };

  const hasValues = values.accountName.trim().length > 0 || values.accountId.trim().length > 0;
  const isWeb = Platform.OS === 'web';

  return (
    <View style={styles.container}>
      {/* Add New Account button - right aligned */}
      <View style={styles.topRow}>
        <View style={styles.spacer} />
        <TouchableOpacity
          onPress={onAddNewAccount}
          style={styles.addBtn}
          accessibilityLabel="Add new account"
        >
          <Text style={styles.addBtnText}>➕ Add New Account</Text>
        </TouchableOpacity>
      </View>

      {/* Search inputs */}
      <View style={styles.searchRow}>
        <View style={styles.inputGroup}>
          <Text style={styles.label}>Account Name</Text>
          <TextInput
            style={styles.input}
            value={values.accountName}
            onChangeText={v => setField('accountName', v)}
            autoCapitalize="words"
            returnKeyType="search"
            onSubmitEditing={handleSearch}
            placeholder="Enter account name"
            placeholderTextColor={EDITOR_COLORS.placeholder}
          />
        </View>

        <View style={styles.spacer} />

        <View style={styles.inputGroup}>
          <Text style={styles.label}>Account Id</Text>
          <TextInput
            style={styles.input}
            value={values.accountId}
            onChangeText={v => setField('accountId', v)}
            autoCapitalize="none"
            returnKeyType="search"
            onSubmitEditing={handleSearch}
            placeholder="Enter account ID"
            placeholderTextColor={EDITOR_COLORS.placeholder}
          />
        </View>

        <View style={styles.spacer} />

        <View style={styles.checkboxGroup}>
          {isWeb ? (
            <View style={styles.checkboxRow}>
              {React.createElement('input', {
                type: 'checkbox',
                checked: values.showAll,
                onChange: (event: any) => setField('showAll', !!event?.target?.checked),
                style: { ...webCheckboxStyle, marginRight: 6 },
                'aria-label': 'Show all accounts',
              })}
              <Text style={styles.checkboxLabel}>Show All</Text>
            </View>
          ) : (
            <Pressable
              onPress={() => setField('showAll', !values.showAll)}
              style={styles.checkboxRow}
              accessibilityRole="checkbox"
              accessibilityLabel="Show all accounts"
            >
              <View style={[styles.checkbox, values.showAll && styles.checkboxChecked]}>
                {values.showAll && <Text style={styles.checkmark}>✓</Text>}
              </View>
              <Text style={styles.checkboxLabel}>Show All</Text>
            </Pressable>
          )}
        </View>

        <View style={styles.spacer} />

        <TouchableOpacity
          onPress={handleSearch}
          style={[styles.searchBtn, isLoading && styles.searchBtnDisabled]}
          disabled={isLoading}
          accessibilityLabel="Search accounts"
        >
          {isLoading ? (
            <ActivityIndicator size="small" color={EDITOR_COLORS.submitText} />
          ) : (
            <Text style={styles.searchBtnText}>🔍 Search</Text>
          )}
        </TouchableOpacity>

        {hasValues && (
          <TouchableOpacity
            onPress={handleClear}
            style={styles.clearBtn}
            accessibilityLabel="Clear search"
          >
            <Text style={styles.clearBtnText}>Clear</Text>
          </TouchableOpacity>
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    paddingVertical: 16,
    paddingHorizontal: 20,
    backgroundColor: EDITOR_COLORS.background,
    borderBottomWidth: 1,
    borderBottomColor: EDITOR_COLORS.separator,
  },
  topRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    marginBottom: 12,
  },
  spacer: {
    width: 12,
  },
  addBtn: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    backgroundColor: EDITOR_COLORS.submitBackground,
    borderRadius: 6,
    alignItems: 'center',
    justifyContent: 'center',
  },
  addBtnText: {
    ...editorText,
    color: EDITOR_COLORS.submitText,
  },
  searchRow: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    flexWrap: 'wrap',
  },
  inputGroup: {
    minWidth: 180,
    flex: 1,
  },
  label: {
    ...editorText,
    color: EDITOR_COLORS.textSecondary,
    marginBottom: 4,
  },
  input: {
    ...editorText,
    height: 36,
    paddingHorizontal: 10,
    borderWidth: 1,
    borderColor: EDITOR_COLORS.controlBorder,
    borderRadius: 6,
    backgroundColor: EDITOR_COLORS.background,
    color: EDITOR_COLORS.textPrimary,
  },
  checkboxGroup: {
    justifyContent: 'flex-end',
    paddingBottom: 4,
  },
  checkboxRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  checkbox: {
    width: 20,
    height: 20,
    borderWidth: 2,
    borderColor: EDITOR_COLORS.controlBorder,
    borderRadius: 4,
    backgroundColor: EDITOR_COLORS.background,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 6,
  },
  checkboxChecked: {
    backgroundColor: EDITOR_COLORS.accent,
    borderColor: EDITOR_COLORS.accent,
  },
  checkmark: {
    color: EDITOR_COLORS.background,
    fontSize: 14,
    fontWeight: 'bold',
  },
  checkboxLabel: {
    ...editorText,
    color: EDITOR_COLORS.textSecondary,
  },
  searchBtn: {
    paddingHorizontal: 20,
    paddingVertical: 8,
    backgroundColor: EDITOR_COLORS.submitBackground,
    borderRadius: 6,
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 100,
  },
  searchBtnDisabled: {
    opacity: 0.6,
  },
  searchBtnText: {
    ...editorText,
    color: EDITOR_COLORS.submitText,
  },
  clearBtn: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    marginLeft: 8,
    backgroundColor: EDITOR_COLORS.submitBackgroundDisabled,
    borderRadius: 6,
    alignItems: 'center',
    justifyContent: 'center',
  },
  clearBtnText: {
    ...editorText,
    color: EDITOR_COLORS.textSecondary,
  },
});
